import { useParams, Navigate } from 'react-router-dom';
import { BlogHeader } from '@/components/BlogHeader';
import { BlogContent } from '@/components/BlogContent';
import { blogPosts } from '@/data/blogPosts';
import { useSEO } from '@/hooks/useSEO';
import { Calendar, Clock, Share2, Twitter, Facebook, Linkedin } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useEffect } from 'react';
const BlogPost = () => {
  const {
    slug
  } = useParams<{
    slug: string;
  }>();
  const post = blogPosts.find(p => p.slug === slug && p.published);
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);
  if (!post) {
    return <Navigate to="/blog" replace />;
  }
  const postUrl = `https://diskover.ovh/blog/${post.slug}`;
  const shareText = `Check out: ${post.title}`;
  useSEO({
    title: `${post.title} | DiskOver Blog`,
    description: post.description,
    keywords: post.tags.join(', '),
    ogTitle: post.title,
    ogDescription: post.description,
    canonical: postUrl
  });
  useEffect(() => {
    // JSON-LD for Article
    const articleSchema = {
      '@context': 'https://schema.org',
      '@type': 'BlogPosting',
      headline: post.title,
      description: post.description,
      image: `https://diskover.ovh${post.featuredImage}`,
      datePublished: post.date,
      dateModified: post.date,
      author: {
        '@type': 'Organization',
        name: post.author
      },
      publisher: {
        '@type': 'Organization',
        name: 'DiskOver',
        logo: {
          '@type': 'ImageObject',
          url: 'https://diskover.ovh/logo.png'
        }
      },
      mainEntityOfPage: {
        '@type': 'WebPage',
        '@id': postUrl
      }
    };
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify(articleSchema);
    document.head.appendChild(script);
    return () => {
      document.head.removeChild(script);
    };
  }, [post, postUrl]);
  const shareOnTwitter = () => {
    window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(postUrl)}`, '_blank');
  };
  const shareOnFacebook = () => {
    window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(postUrl)}`, '_blank');
  };
  const shareOnLinkedIn = () => {
    window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(postUrl)}`, '_blank');
  };
  return <div className="min-h-screen bg-background">
      <BlogHeader showBackButton />

      {/* Article Header */}
      <article className="container mx-auto px-4 py-12">
        <div className="max-w-4xl mx-auto">
          {/* Breadcrumb */}
          <nav className="text-sm text-muted-foreground mb-8">
            <a href="/" className="hover:text-primary transition-colors">Home</a>
            {' / '}
            <a href="/blog" className="hover:text-primary transition-colors">Blog</a>
            {' / '}
            <span className="text-foreground">{post.title}</span>
          </nav>

          {/* Categories */}
          <div className="flex flex-wrap gap-2 mb-6">
            {post.category.map(cat => <span key={cat} className="text-xs font-medium px-3 py-1.5 rounded-full bg-primary/10 text-primary">
                {cat}
              </span>)}
          </div>

          {/* Title */}
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-foreground leading-tight mb-6">
            {post.title}
          </h1>

          {/* Meta */}
          <div className="flex flex-wrap items-center gap-6 text-sm text-muted-foreground mb-8 pb-8 border-b border-border/50">
            <div className="flex items-center gap-2">
              <Calendar className="w-4 h-4" />
              <time dateTime={post.date}>
                {new Date(post.date).toLocaleDateString('en-US', {
                month: 'long',
                day: 'numeric',
                year: 'numeric'
              })}
              </time>
            </div>
            <div className="flex items-center gap-2">
              <Clock className="w-4 h-4" />
              <span>{post.readTime} read</span>
            </div>
            <div className="ml-auto flex items-center gap-2">
              <Share2 className="w-4 h-4" />
              <span className="mr-2">Share:</span>
              <Button variant="ghost" size="sm" onClick={shareOnTwitter} className="h-8 w-8 p-0" aria-label="Partager sur Twitter">
                <Twitter className="w-4 h-4" />
              </Button>
              <Button variant="ghost" size="sm" onClick={shareOnFacebook} className="h-8 w-8 p-0" aria-label="Partager sur Facebook">
                <Facebook className="w-4 h-4" />
              </Button>
              <Button variant="ghost" size="sm" onClick={shareOnLinkedIn} className="h-8 w-8 p-0" aria-label="Partager sur LinkedIn">
                <Linkedin className="w-4 h-4" />
              </Button>
            </div>
          </div>

          {/* Featured Image */}
          

          {/* Content */}
          <BlogContent sections={post.content.sections} />

          {/* Tags */}
          <div className="mt-16 pt-8 border-t border-border/50">
            <div className="flex flex-wrap gap-2">
              {post.tags.map(tag => <span key={tag} className="text-xs px-3 py-1.5 rounded-full bg-muted text-muted-foreground hover:bg-primary/10 hover:text-primary transition-colors cursor-pointer">
                  #{tag}
                </span>)}
            </div>
          </div>
        </div>
      </article>
    </div>;
};
export default BlogPost;